import { Link, useParams } from "react-router-dom";
import { CalendarDays, ArrowRight, ArrowLeft, ExternalLink, FileDown, Tag } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { ARTICLES, RESOURCES } from "@/data/content";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

const DOWNLOADS = [
  { title: "Plaquette de présentation de l'association", meta: "PDF — à imprimer et partager", href: "/documents/plaquette-aide-monde-sante.pdf", id: "download-plaquette" },
  { title: "Comprendre la maladie de Waldmann", meta: "PDF — fiche d'information grand public", href: "/documents/fiche-maladie-waldmann.pdf", id: "download-fiche" },
];

const Actualites = () => (
  <div data-testid="actualites-page">
    <PageHero
      eyebrow="Actualités & ressources"
      title="Nos actualités, conseils et liens utiles"
      description="Articles, conseils pratiques, événements de l'association et ressources fiables sur la maladie de Waldmann."
      testid="actualites-title"
    />

    <section className="py-16 md:py-24">
      <div className="container-site">
        <Reveal>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2C59]">
            Les derniers articles
          </h2>
        </Reveal>
        <div className="mt-10 grid gap-8 md:grid-cols-2">
          {ARTICLES.map((a, i) => (
            <Reveal key={a.slug} delay={(i % 2) * 0.1}>
              <Link
                to={`/actualites/${a.slug}`}
                data-testid={`article-card-${a.slug}`}
                className="card-site group block h-full !p-8"
              >
                <div className="flex flex-wrap items-center gap-3 text-xs font-bold">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EFF6FF] px-3 py-1.5 text-[#0284C7]">
                    <Tag size={12} /> {a.category}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-slate-400">
                    <CalendarDays size={13} /> {fmtDate(a.date)}
                  </span>
                </div>
                <h3 className="mt-4 font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#0F2C59] transition-colors group-hover:text-[#0284C7]">
                  {a.title}
                </h3>
                <p className="mt-3 text-sm sm:text-base leading-relaxed text-slate-600">{a.excerpt}</p>
                <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#FF6B6B]">
                  Lire l'article <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[#EFF6FF] py-16 md:py-24">
      <div className="container-site grid gap-12 lg:grid-cols-2">
        <Reveal>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2C59]">
            Liens utiles
          </h2>
          <p className="mt-3 text-base text-slate-600">
            Des sources d'information fiables et reconnues sur les maladies rares.
          </p>
          <div className="mt-8 space-y-4">
            {RESOURCES.map((r, i) => (
              <a
                key={i}
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`resource-link-${i}`}
                className="card-site group flex items-center justify-between gap-4 !p-6"
              >
                <div>
                  <h3 className="font-heading text-base font-bold text-[#0F2C59] transition-colors group-hover:text-[#0284C7]">
                    {r.title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{r.description}</p>
                </div>
                <ExternalLink size={19} className="shrink-0 text-slate-400 transition-colors group-hover:text-[#0284C7]" />
              </a>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2C59]">
            Documents à télécharger
          </h2>
          <p className="mt-3 text-base text-slate-600">
            À imprimer, partager ou garder sous la main.
          </p>
          <div className="mt-8 space-y-4">
            {DOWNLOADS.map((d) => (
              <a
                key={d.id}
                href={d.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={d.id}
                className="card-site group flex items-center gap-5 !p-6"
              >
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#0F2C59] text-white transition-colors group-hover:bg-[#FF6B6B]">
                  <FileDown size={21} />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-[#0F2C59]">{d.title}</h3>
                  <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">{d.meta}</p>
                </div>
              </a>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border-2 border-dashed border-slate-300 bg-white/60 p-6">
            <p className="text-sm leading-relaxed text-slate-600">
              Vous cherchez une information précise sur la maladie de Waldmann ?{" "}
              <Link to="/contact" data-testid="resources-contact-link" className="font-bold text-[#0284C7] underline underline-offset-2">
                Contactez-nous
              </Link>
              , nous vous orienterons vers la bonne ressource.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export const ArticlePage = () => {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="container-site py-40 text-center" data-testid="article-not-found">
        <h1 className="font-heading text-3xl font-bold text-[#0F2C59]">Article introuvable</h1>
        <Link to="/actualites" className="btn-outline-navy mt-8">
          <ArrowLeft size={16} /> Retour aux actualités
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="article-page">
      <PageHero
        eyebrow={article.category}
        title={article.title}
        description={`Publié le ${fmtDate(article.date)}`}
        testid="article-title"
      />
      <section className="py-14 md:py-20">
        <div className="container-site max-w-3xl">
          <Reveal>
            <Link
              to="/actualites"
              data-testid="article-back-link"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#0284C7] hover:text-[#0F2C59] transition-colors"
            >
              <ArrowLeft size={16} /> Toutes les actualités
            </Link>
            <div className="mt-8 space-y-6">
              {article.content.map((p, i) => (
                <p key={i} className="text-base sm:text-lg leading-relaxed text-slate-700">
                  {p}
                </p>
              ))}
            </div>
            <div className="mt-12 rounded-[2rem] bg-[#0F2C59] p-8 text-center sm:p-10">
              <p className="font-heading text-xl font-bold text-white">
                Envie de soutenir les malades de Waldmann ?
              </p>
              <Link to="/faire-un-don" data-testid="article-donate-button" className="btn-don mt-6">
                Faire un don
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Actualites;
